import { httpClient } from "infra/axios/axios-api";
import { Document } from "domain/entities/document";
import { DocumentRequired } from "domain/entities/document-required";
import { EMAIL } from "domain/enum/email";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { DocumentRequiredInputFilter } from "domain/models/document-required-input-filter";
import { translateText } from "infra/translation/translate";
import {
  useDeleteDocumentMutation,
  useDocumentByFilter,
} from "presentation/hooks/use-query-document";
import { useDocumentRequiredByFilter } from "presentation/hooks/use-query-document-required";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useEmployeeStore } from "presentation/stores/use-employee-store";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import DocumentUploadDialog from "./DocumentFormUpload";
import PdfViewerDialog from "presentation/components/Shared/PdfViewerDialog";

interface EmployeeDocumentsProps {
  canEdit: boolean;
}

const DocumentGrid: React.FC<EmployeeDocumentsProps> = ({ canEdit }) => {
  const toast = useRef<Toast>(null);
  const { employee, setEmployee } = useEmployeeStore();
  const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [showDoc, setShowDoc] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [documentsFiltered, setDocumentsFiltered] = useState<Document[]>([]);
  const [missingDocs, setMissingDocs] = useState<DocumentRequired[]>([]);

  const { data: documentsEmployee, refetch: refetchDocuments } =
    useDocumentByFilter({
      limit: 100,
      sortBy: "modifiedAt",
      sortOrder: "desc",
      employeeId: employee?.id,
      source: "employee",
    } as DocumentInputFilter);

  const { data: documentsRequireds } = useDocumentRequiredByFilter({
    limit: 100,
    sortBy: "id",
    sortOrder: "asc",
    contractType: employee?.contractType,
  } as DocumentRequiredInputFilter);

  const { mutate: deleteDocument } = useDeleteDocumentMutation();
  const { email: userEmployeeEmail } = useAuthStore();

  const getDocumentFromContabo = async (fullPath: string) => {
    if (!fullPath) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "O caminho do documento não foi encontrado.",
        life: 3000,
      });
      return;
    }

    try {
      setS3PathToDisplay(fullPath);
    } catch (error) {
      console.error("Erro ao buscar a URL assinada:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Ocorreu um erro ao tentar carregar o documento.",
        life: 3000,
      });
    }
  };

  const dateBodyTemplate = (rowData: Document) => {
    return formatDateToPT(rowData.modifiedAt || rowData.createdAt);
  };

  const handleDelete = (document: Document) => {
    if (!document.id || !document.fullPath) return;

    // Primeiro, apaga o ficheiro do armazenamento
    httpClient
      .delete(`/assets/${document.fullPath}`)
      .then(() => {
        // Se o ficheiro for apagado com sucesso, apaga o registo da BD
        const docToDelete: Document = {
          ...document,
          lastModifiedBy: userEmployeeEmail ?? EMAIL,
        };
        deleteDocument(docToDelete, {
          onSuccess: () => {
            toast.current?.show({
              severity: "success",
              summary: "Sucesso",
              detail: "Documento eliminado com sucesso.",
              life: 3000,
            });
            refetchDocuments();
          },
          onError: () => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: "Falha ao eliminar o registo do documento.",
              life: 3000,
            });
          },
        });
      })
      .catch(() => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao eliminar o ficheiro do armazenamento.",
          life: 3000,
        });
      });
  };

  const confirmDelete = (document: Document) => {
    confirmDialog({
      message: "Tem a certeza que deseja eliminar este documento?",
      header: "Confirmação de Eliminação",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Sim, eliminar",
      rejectLabel: "Não",
      accept: () => handleDelete(document),
    });
  };

  const actionBodyTemplate = (rowData: Document) => {
    return (
      <div className="spacing-icons">
        <i
          className="pi pi-eye cursor-pointer"
          onClick={() => getDocumentFromContabo(rowData.fullPath)}
        />
        {canEdit && (
          <i
            className="pi pi-trash cursor-pointer"
            onClick={() => confirmDelete(rowData)}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (documentsEmployee?.data && documentsEmployee.data.length > 0) {
      const documentsFiltered =
        documentsEmployee?.data?.filter(
          (item) => item.timesheetDay === null && item.invoice === null
        ) || [];
      setDocumentsFiltered([...documentsFiltered]);
    }
  }, [documentsEmployee]);

  useEffect(() => {
    if (documentsRequireds?.data) {
      const employeeDocTypeIds =
        documentsEmployee?.data?.map((doc) => doc.documentType.id) || [];

      const missing = documentsRequireds.data.filter(
        (requiredDoc) =>
          !employeeDocTypeIds.includes(requiredDoc.documentType.id)
      );

      setMissingDocs(missing);
    }
  }, [documentsRequireds, documentsEmployee]);

  const header = (
    <div className="flex justify-content-between align-items-center">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Pesquisar por nome"
        />
      </IconField>
      {canEdit && (
        <Button
          type="button"
          label="Novo Documento"
          icon="pi pi-plus"
          onClick={() => setShowUploadDialog(true)}
        />
      )}
    </div>
  );

  return (
    <div className="card">
      <Toast ref={toast} />
      {missingDocs.length > 0 && (
        <div
          className="mb-4"
          style={{
            backgroundColor: "#fff3cd",
            padding: "1rem",
            borderRadius: "4px",
          }}
        >
          <p className="font-bold">Documentos Obrigatórios em Falta:</p>
          <div className="flex flex-wrap gap-2">
            {missingDocs.map((doc) => (
              <Tag
                key={doc.documentType.id}
                value={translateText(doc.documentType.name, "pt")}
                severity="warning"
              />
            ))}
          </div>
        </div>
      )}
      <ConfirmDialog />
      <DocumentUploadDialog
        visible={showUploadDialog}
        onHide={() => setShowUploadDialog(false)}
        onUploadSuccess={refetchDocuments}
      />
      <DataTable
        value={documentsFiltered || []}
        size="small"
        header={header}
        globalFilter={globalFilter}
        globalFilterFields={["documentType.name"]}
        paginator
        rowsPerPageOptions={[5, 10, 15]}
        rows={5}
        emptyMessage="Nenhum documento associado."
      >
        <Column
          field="documentType.name"
          header={translateText("Document Type", "pt")}
          sortable
          style={{ width: "40%" }}
        />
        <Column
          header={translateText("Modification Date", "pt")}
          body={dateBodyTemplate}
          field="modifiedAt"
          sortable
          style={{ width: "40%" }}
        />
        <Column
          header={translateText("Actions", "pt")}
          body={actionBodyTemplate}
          style={{ width: "10rem", textAlign: "center" }}
        />
      </DataTable>

      <Dialog
        header="Visualização do Documento"
        visible={showDoc}
        style={{ width: "80vw", height: "90vh" }}
        onHide={() => setShowDoc(false)}
        modal
      >
        {docUrl &&
          (docUrl.toLowerCase().includes(".pdf") ? (
            <iframe
              src={`${docUrl}#navpanes=0`}
              width="100%"
              height="99%"
              title="Document Viewer"
              style={{ border: "none" }}
            />
          ) : (
            <img
              src={docUrl}
              alt="Document"
              style={{ width: "100%", height: "auto" }}
            />
          ))}
      </Dialog>
      {s3PathToDisplay && (
        <PdfViewerDialog
          s3Path={s3PathToDisplay}
          onHide={() => setS3PathToDisplay(null)}
        />
      )}
    </div>
  );
};

export default DocumentGrid;
